import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  eachDayOfInterval,
  startOfWeek,
  getDay,
} from 'date-fns';
import { useQuery, useMutation } from '@apollo/client';
import { LIST_CALENDARS } from '~/services/calendar';
import { CANCEL_SCHEDULE } from '~/services/schedule';
import { publicClient } from '~/services';
import AppointmentDialog from './AppointmentDialog';
import ClientCancelAppointmentDialog from '../../shared/ClientCancelAppointmentDialog';
import { useTranslation } from 'react-i18next';
import { es, enUS, fr, de } from 'date-fns/locale';
import { Schedule } from '~/types';
import Calendar from './Calendar';

const SchedulerScreen = ({
  scheduleData,
  typeName,
  isPreviewMode = false,
  agentId,
  calendarsData: previewCalendarsData,
}: {
  scheduleData: any | Schedule;
  typeName?: any;
  isPreviewMode?: boolean;
  agentId?: string;
  calendarsData?: any[];
}) => {
  const [t] = useTranslation('calendar');
  const { schedule_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCalendar = location.state?.selectedCalendars?.[0];

  // Create preview schedule data using real calendar data
  const previewScheduleData = isPreviewMode && previewCalendarsData && previewCalendarsData.length > 0 ? {
    name: 'Vista Previa de Agendas',
    duration_of_meeting: previewCalendarsData[0]?.duration_of_meeting || 30,
    agent_id: agentId,
  } : null;

  // For preview mode, use the selected calendar from navigation state if available, otherwise fallback to first calendar
  const previewSelectedCalendar = isPreviewMode && previewCalendarsData && previewCalendarsData.length > 0 ? 
    (selectedCalendar || previewCalendarsData[0]) : null;

  // Use real data or preview data based on preview mode
  const initialScheduleData = isPreviewMode ? previewScheduleData : scheduleData;
  const currentSelectedCalendar = isPreviewMode ? previewSelectedCalendar : selectedCalendar;

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDayId, setCalendarDayId] = useState<string | null>(null);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [isPast, setIsPast] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [selectedTime, setSelectedTime] = useState<{
    realTime: string;
    utcTime: string;
  } | null>(null);
  const [utcOffset, setUtcOffset] = useState(
    new Date().getTimezoneOffset() / -60,
  );
  
  // States for cancellation functionality
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isPreviewMode && !currentSelectedCalendar) {
      navigate(`/calendar/${schedule_id}`);
      return;
    }
  }, [currentSelectedCalendar, schedule_id, navigate, isPreviewMode]);

  const {
    data: calendarsData,
    loading: calendarsLoading,
    refetch: refetchCalendars,
  } = useQuery(LIST_CALENDARS, {
    variables: {
      data: {
        calendar_rulers_id: currentSelectedCalendar?._id,
      },
    },
    client: publicClient,
    skip: !currentSelectedCalendar, // Remove the isPreviewMode condition to allow querying in preview mode
  });

  const [cancelScheduleMutation] = useMutation(CANCEL_SCHEDULE, {
    client: publicClient,
  });

  // Use real calendar data for both preview and normal mode
  const availableDays = calendarsData?.list_calendars?.data.map(
    (calendar: { date: any }) => calendar.date.split('T')[0],
  );

  const [currentScheduleData, setCurrentScheduleData] = useState(initialScheduleData);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [times, setTimes] = useState<{ realTime: string; utcTime: string }[]>(
    [],
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [slotIndex, setSlotIndex] = useState<number | null>(null);

  useEffect(() => {
    if (
      currentScheduleData &&
      availableDays &&
      currentScheduleData?.start_date
    ) {
      const { date, hour, minute } = currentScheduleData.start_date;
      const selectedDateObj = new Date(date);
      selectedDateObj.setHours(hour, minute);
      const isoDate = new Date(date).toISOString().split('T')[0];
      const availableDayIndex = availableDays.indexOf(isoDate);
      if (availableDayIndex !== -1 && !selectedDate) {
        handleDateSelection(selectedDateObj, availableDayIndex);
      }
      if (new Date(date) < new Date()) {
        setIsPast(true);
      }
    } else {
      if (!selectedDate && availableDays && availableDays.length > 0) {
        const firstAvailableDay = availableDays[0];
        const date = new Date(firstAvailableDay);
        date.setDate(date.getDate() + 1);
        const index = 0;
        handleDateSelection(date, index);
      }
    }

    // Check if appointment is rejected/canceled
    if (currentScheduleData?.status === 'REJECTED') {
      setIsRejected(true);
    }
  }, [availableDays, currentScheduleData, forceUpdate]);

  if (calendarsLoading) {
    return (
      <div className="mx-auto mt-8 flex h-full w-full max-w-[1024px] flex-col items-center justify-center">
        <div className="skeleton-brand w-full max-w-md h-48 rounded-lg"></div>
      </div>
    );
  }

  if (!isPreviewMode && (!currentScheduleData || !calendarsData)) {
    return (
      <div className="mx-auto mt-8 flex h-full w-full max-w-[1024px] flex-col items-center justify-center">
        <div className="alert alert-brand-error">
          <p className="text-brand-body">{t('schedule_error')}</p>
        </div>
      </div>
    );
  }

  if (isPreviewMode && !calendarsData) {
    return (
      <div className="mx-auto mt-8 flex h-full w-full max-w-[1024px] flex-col items-center justify-center">
        <div className="alert alert-brand-warning">
          <p className="text-brand-body">
            No hay datos de calendario disponibles para la vista previa.
          </p>
        </div>
      </div>
    );
  }

  // Handle preview mode with no calendars
  if (isPreviewMode && (!previewCalendarsData || previewCalendarsData.length === 0)) {
    return (
      <div className="mx-auto mt-8 flex h-full w-full max-w-[1024px] flex-col items-center justify-center">
        <div className="alert alert-brand-warning">
          <div>
            <h3 className="font-semibold">Vista Previa No Disponible</h3>
            <p className="text-sm mt-1">
              No hay agendas disponibles para mostrar en la vista previa. 
              Por favor, crea al menos una agenda primero.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const daysOfWeek = [
    t('monday'),
    t('tuesday'),
    t('wednesday'),
    t('thursday'),
    t('friday'),
    t('saturday'),
    t('sunday'),
  ];
  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const startDayOfWeek = getDay(startOfMonth(currentMonth));
  const emptyDays = Array.from({
    length: startDayOfWeek === 0 ? 6 : startDayOfWeek - 1,
  }).map((_, index) => <div key={index} className="h-10 w-10"></div>);

  function handleDateSelection(date: any, index: number) {
    setSelectedDate(date);
    setCalendarDayId(calendarsData?.list_calendars?.data[index]._id);
    const slots = calendarsData?.list_calendars?.data[index].slots;
    const duration = currentScheduleData?.duration_of_meeting || 30;
    const times = [];
    for (let i = 0; i < slots.length; i++) {
      if (slots[i] === 0) {
        const hours = Math.floor((i * duration) / 60);
        const minutes = (i * duration) % 60;
        const time = new Date();
        time.setHours(hours, minutes);

        const realTime = `${time.getHours().toString().padStart(2, '0')}:${time
          .getMinutes()
          .toString()
          .padStart(2, '0')}`;
        time.setHours(
          time.getHours() + ((currentSelectedCalendar?.utc || 0) - utcOffset),
        );
        const utcTime = `${time.getHours().toString().padStart(2, '0')}:${time
          .getMinutes()
          .toString()
          .padStart(2, '0')}`;

        times.push({
          realTime,
          utcTime,
        });
      }
    }
    setTimes(times);
  }

  // Add this function to remove a time from the times list
  const removeTime = (realTime: string) => {
    setTimes((prevTimes) =>
      prevTimes.filter((time) => time.realTime !== realTime),
    );
  };

  // Cancellation functions
  const handleCancelAppointment = () => {
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancellation = async (reason: string) => {
    if (!currentScheduleData?.id && !schedule_id) return;

    setCancelLoading(true);
    try {
      const { data: cancelResult } = await cancelScheduleMutation({
        variables: {
          data: {
            schedule_id: currentScheduleData?.id || schedule_id,
            cancel_reason: reason,
            cancel_by: 'USER',  
          }
        }
      });

      if (cancelResult?.cancel_schedule?.hasError) {
        throw new Error(cancelResult.cancel_schedule.message || 'Error canceling appointment');
      }

      setSuccessMessage(t('appointment_canceled_successfully') || 'Cita cancelada exitosamente');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

      // Redirect to home or show success state
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error canceling appointment:', error);
      alert(`Error al cancelar la cita: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setCancelLoading(false);
      setIsCancelDialogOpen(false);
    }
  };

  const handleCloseCancelDialog = () => {
    setIsCancelDialogOpen(false);
  };

  const localeMap = {
    es: es,
    en: enUS,
  };
  const userLocale = (navigator.language.split('-')[0] ||
    'es') as keyof typeof localeMap;
  const locale = localeMap[userLocale] || enUS;

  return (
    <div className="flex min-h-screen flex-col bg-base-100 md:flex-row">
      {/* information Section */}
      <div className="mx-4 mb-4 mt-4 md:w-1/3">
        <div className="card-brand">
          <div className="card-body">
            <button
              onClick={() => window.history.back()}
              className="btn btn-ghost btn-sm mb-4 self-start"
              disabled={isPast || isRejected}
            >
              ← {t('back')}
            </button>
            <h1 className="text-brand-heading text-2xl mb-4">
              {t('greeting', { name: currentScheduleData?.name || 'Usuario' })}
            </h1>
            <p className="text-brand-body mb-6">{t('welcome_message')}</p>
            
            <div className="divider"></div>
            
            <div>
              <h2 className="text-brand-heading text-xl mb-4">
                {t('meeting_information')}
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-brand-body font-semibold">{t('modality')}</p>
                  <div className="badge badge-primary">
                    {currentSelectedCalendar?.meetingType === 'IN_PERSON'
                      ? t('in_person')
                      : t('virtual')}
                  </div>
                </div>
                {currentSelectedCalendar?.meetingDetails?.headquarters && (
                  <div className="flex justify-between items-start">
                    <p className="text-brand-body font-semibold">{t('location')}</p>
                    <div className="text-right text-brand-caption">
                      <p>{currentSelectedCalendar?.meetingDetails?.headquarters}</p>
                      <p className="text-sm opacity-70">
                        {currentSelectedCalendar?.meetingDetails?.address}
                      </p>
                    </div>
                  </div>
                )}
                {currentSelectedCalendar?.meetingDetails?.description && (
                  <div>
                    <p className="text-brand-body font-semibold mb-2">{t('description')}</p>
                    <p className="text-brand-caption">
                      {currentSelectedCalendar?.meetingDetails?.description}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Cancel appointment button for scheduled appointments */}
              {currentScheduleData?.start_date && !isPreviewMode && !isPast && !isRejected && currentScheduleData?.status === 'ACCEPTED' && (
                <div className="mt-6">
                  <button
                    onClick={handleCancelAppointment}
                    className="btn btn-outline btn-error rounded-full w-full"
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? (
                      <span className="loading loading-spinner loading-sm"></span>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t('cancel_appointment') || 'Cancelar cita'}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Section */}
      <div className="mb-4 ml-4 mr-4 mt-4 md:w-2/3">
        <div className="card-brand">
          <div className="card-body">
            {currentScheduleData?.start_date && !isPreviewMode && (
              <div className="mb-6">
                {isRejected ? (
                  <>
                    <div className="alert alert-brand-error">
                      <div>
                        <h3 className="font-bold">{t('appointment_canceled')}</h3>
                        <p className="text-sm mt-1">
                          Tu cita programada para el{' '}
                          {(() => {
                            const date = new Date(currentScheduleData?.start_date.date);
                            date.setHours(date.getHours() + utcOffset);
                            const offsetDifference = currentSelectedCalendar.utc - utcOffset;
                            date.setUTCHours(date.getUTCHours() + offsetDifference);
                            return date.toLocaleDateString('es-ES', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          })()}{' '}
                          ha sido cancelada.
                          {currentScheduleData.cancel_reason && (
                            <>
                              <br />
                              <span className="text-xs opacity-80 mt-1 block">
                                <strong>{t('canceled_reason')}:</strong> {currentScheduleData.cancel_reason}
                              </span>
                            </>
                          )}
                          {currentScheduleData.cancel_at && (
                            <span className="text-xs opacity-80 mt-1 block">
                              <strong>{t('canceled_on')}:</strong> {new Date(currentScheduleData.cancel_at).toLocaleDateString('es-ES', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    {/* Reschedule button for rejected appointments */}
                    <div className="mt-4">
                      <button
                        className="btn btn-primary rounded-full w-full"
                        onClick={() => navigate(`/calendar/${schedule_id}/create_new_schedule`)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('reschedule_appointment')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className={`alert ${
                    isPast ? 'alert-brand-warning' : 'alert-brand-success'
                  }`}>
                    <div>
                      <h3 className="font-bold">
                        {isPast ? t('past_booking') : t('current_booking')}
                      </h3>
                      <p className="text-sm mt-1">
                        {(() => {
                          const date = new Date(currentScheduleData?.start_date.date);
                          date.setHours(date.getHours() + utcOffset);
                          const offsetDifference = currentSelectedCalendar.utc - utcOffset;
                          date.setUTCHours(date.getUTCHours() + offsetDifference);
                          return date.toLocaleString('en-US', { timeZone: 'UTC' });
                        })()}{' '}
                        -{' '}
                        {currentSelectedCalendar?.meetingType === 'IN_PERSON'
                          ? t('in_person')
                          : t('virtual')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isPreviewMode && (
              <div className="mb-6">
                <div className="alert bg-brand-gradient text-white">
                  <div>
                    <h3 className="font-bold">🔍 Vista Previa del Calendario</h3>
                    <p className="text-sm mt-1 opacity-90">
                      Este es un ejemplo de cómo se vería tu calendario. Los clientes no pueden agendar citas reales en este modo.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <h2 className="text-brand-heading text-xl mb-6">
              {t('select_day_time')}
            </h2>

            {/* Calendar */}
            <Calendar
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              availableDays={availableDays}
              handleDateSelection={handleDateSelection}
              times={times}
              selectedTime={selectedTime}
              setSelectedTime={setSelectedTime}
              locale={locale}
              setIsDialogOpen={setIsDialogOpen}
              isPast={isPast || isRejected}
              isPreviewMode={isPreviewMode}
            />
          </div>
        </div>
      </div>

      {isDialogOpen && !isPreviewMode && !isRejected && (
        <AppointmentDialog
          onClose={() => setIsDialogOpen(false)}
          selectedDate={selectedDate}
          selectedTime={selectedTime!}
          schedule_id={schedule_id!}
          utc={utcOffset || 0}
          token={currentScheduleData?.token}
          calendarDayId={calendarDayId!}
          type={currentScheduleData?.start_date ? 1 : 0}
          removeTime={removeTime}
          onScheduleUpdated={(updatedScheduleData) => {
            refetchCalendars();
            setCurrentScheduleData(updatedScheduleData);
          }}
        />
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="toast toast-end toast-top z-50 mr-4 mt-4">
          <div className="alert alert-success text-success-content shadow-lg animate-fadeInRight">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 stroke-current" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">{successMessage}</span>
              </div>
              <button 
                onClick={() => setSuccessMessage(null)} 
                className="ml-4 hover:opacity-75 transition-opacity"
                aria-label="Cerrar notificación"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 stroke-current" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Appointment Dialog */}
      {currentScheduleData && (
        <ClientCancelAppointmentDialog
          isOpen={isCancelDialogOpen}
          onClose={handleCloseCancelDialog}
          appointmentName={currentScheduleData.name || 'Usuario'}
          scheduledDate={currentScheduleData.start_date ? new Date(currentScheduleData.start_date.date) : undefined}
          onConfirmCancel={handleConfirmCancellation}
        />
      )}
    </div>
  );
};

export default SchedulerScreen;
